import { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { ulid } from 'ulid';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormActionButtons } from '@/components/layout/form-action-buttons';
import type { ScenarioViewModel, CreateScenarioMutation, CreateScenarioMutationVariables } from '@/graphql/generated';
import { ConditionOperator } from '@/graphql/generated';
import { extractCheckResultError } from '@/lib/checkResult';
import { AutocompleteCreateFormProps } from './autocomplete-create-form';
import { SCENARIO_CREATE_MUTATION } from '../../pages/scenarios/scenarios.gql';

export function ScenarioCreateForm({ onSuccess }: AutocompleteCreateFormProps<ScenarioViewModel>) {
	const { t } = useTranslation('scenarios');
	const [submissionError, setSubmissionError] = useState<string | null>(null);
	const [name, setName] = useState('');
	const idempotentScenarioId = useMemo(() => ulid(), []);

	const [createScenario, { loading: isSubmitting }] = useMutation<
		CreateScenarioMutation,
		CreateScenarioMutationVariables
	>(SCENARIO_CREATE_MUTATION, {
		// Prevent cache interactions that might trigger refreshes
		fetchPolicy: 'no-cache'
	});

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setSubmissionError(null);

		if (!name.trim()) {
			setSubmissionError(t('form.scenarioNameRequired'));
			return;
		}

		// Create a minimal scenario with a default "always apply" condition
		const payload = {
			scenarioId: idempotentScenarioId,
			name: name.trim(),
			scenarioConditions: [
				{
					conditionId: ulid(),
					operator: ConditionOperator.Include,
					condition: {
						// Create a basic "always apply" condition
						weeklyDateSetId: 1, // Assuming this exists - daily schedule
						appliesOnHolidays: true,
						appliesToSpecificVehicleTypes: false,
						appliesToSpecificCustomerCategories: false,
						appliesToSpecificPaymentMethods: false,
						isOverstay: false,
						isContractOverstay: false,
						// Required arrays for condition types
						conditionDayOfWeeks: [], // Empty means no specific days restriction
						conditionVehicleTypes: [], // Empty means all vehicle types
						conditionCustomerCategories: [], // Empty means all customer categories
						conditionPaymentMethods: [], // Empty means all payment methods
						// Optional time and duration fields (omitted means no restriction)
					}
				}
			]
		};

		try {
			const { data } = await createScenario({
				variables: { input: payload }
			});

			const message = extractCheckResultError(data?.createScenario);

			if (message) {
				setSubmissionError(message);
				return;
			}

			// Skip success toast in autocomplete context to prevent navigation issues
			// The autocomplete selection will provide user feedback

			// Create a minimal ScenarioViewModel object to pass back
			const newScenario = {
				scenarioId: idempotentScenarioId,
				name: name.trim(),
				facility: null,
				facilityId: null,
				limitPolicies: [],
				pricingPolicies: [],
				scenarioConditions: [],
			} as unknown as ScenarioViewModel;

			onSuccess(newScenario);
		} catch (error) {
			const fallbackMessage =
				error instanceof Error
					? error.message
					: 'Unexpected error while creating scenario.';
			setSubmissionError(fallbackMessage);
		}
	};

	return (
		<div className="space-y-4">
			{submissionError && (
				<Alert variant="destructive">
					<AlertTitle>{t('form.unableToCreate')}</AlertTitle>
					<AlertDescription>{submissionError}</AlertDescription>
				</Alert>
			)}

			<form onSubmit={handleSubmit} className="space-y-4">
				<div className="space-y-2">
					<Label htmlFor="scenario-name">
						{t('form.scenarioName')} *
					</Label>
					<Input
						id="scenario-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t('form.scenarioNamePlaceholder')}
						className="w-full"
						required
						autoFocus
					/>
					<p className="text-sm text-muted-foreground">
						{t('form.scenarioNameHelper')}
					</p>
				</div>

				<FormActionButtons
					isSubmitting={isSubmitting}
					submitLabel={t('form.createScenario')}
					submitType="submit"
				/>
			</form>
		</div>
	);
}
